import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import useSWR from 'swr';
import {getApplicationPostProcessors} from '../../../api/postprocessor/GetApplicationPostProcessors';
import {DeploymentDetailsInfo} from '../../../types/deployment/DeploymentDetailsInfo';
import {Project} from '../../../types/project/Project';
import {usePostProcessingScript} from '../../PrePostProcessing/PostProcessing/usePostProcessingScript';
import {ISPResult} from '../ISPResult';
import {NoDeployment} from '../NoDeployment';
import {setStreamReady, useDeploy} from '../useDeploy';
import {
  EVENT_TYPE,
  VideoStream,
  VideoStreamController,
  VideoStreamEvent,
  VideoStreamObserver,
  VideoStreamSubject,
} from '../HlsVideoStream';
import './TestInferenceResults.scss';
import {TestResultParameters} from './TestResultParameters';
import {UnfinishedTestResults} from './UnfinishedTestResults';
import {StaticTestResults} from './StaticTestResults';
import {MaxSecondsMessage} from './MaxSecondsMessage';
import {WaitMessage} from './WaitMessage';
import SegmentationLegendView from '../SegmentationResult/SegmentationLegendView';

export type TestInferenceResultsProps = {
  deployment: DeploymentDetailsInfo | null;
  hasTransformation: boolean;
  isStreamingInference: boolean;
  isOtherTaskInProgress: boolean;
  projectData: Project;
  videoController?: VideoStreamController;
  streamSubject: VideoStreamSubject;
  spaId?: string;
  spaPostProcessorId?: string;
  displayRealtimeParameters: boolean;
};

export const TestInferenceResults = ({
  deployment,
  hasTransformation,
  isStreamingInference,
  isOtherTaskInProgress,
  projectData,
  videoController,
  streamSubject,
  spaId,
  spaPostProcessorId,
  displayRealtimeParameters,
}: TestInferenceResultsProps) => {
  const [search] = useState('');
  const [streamConnected, setStreamConnected] = useState(false);
  const [displayWaitMessage, setDisplayWaitMessage] = useState(true);
  const videoDataContainerRef = useRef<HTMLDivElement>(null);
  const [{deployConfig, streamReady}, dispatch] = useDeploy();
  const [isTaskInProgress, setTaskInProgress] = useState(false);
  const projectId = projectData.id;
  const applicationId = deployConfig.deploymentId;

  const {data: postProcessorsResponse} = useSWR(
    projectId && applicationId
      ? [projectId, applicationId, '/postprocessor/get/application']
      : null,
    () => getApplicationPostProcessors({projectId, applicationId, spaPostProcessorId}),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const segmentationLegends = useMemo(
    () => projectData.applications?.[applicationId]?.model?.segmentationLegend || [],
    [projectData, applicationId]
  );

  const postProcessors = postProcessorsResponse?.body || [];
  const browserPostProcessor = postProcessors.find(
    postProcessor =>
      postProcessor.executionContainer === 'BROWSER' &&
      postProcessor.postProcessorType === 'APPLICATION'
  );
  const filePaths = browserPostProcessor?.filePaths || [];
  const fileName = filePaths.find(path => path !== 'metadata.yaml');

  class TestInferenceObserver extends VideoStreamObserver {
    handleEvent(event: VideoStreamEvent) {
      switch (event.type) {
        case EVENT_TYPE.REMOTE_STREAM:
          setTaskInProgress(false);
          dispatch(setStreamReady(true));
          setDisplayWaitMessage(false);
          break;
        case EVENT_TYPE.CONNECTING:
          setTaskInProgress(true);
          break;
        case EVENT_TYPE.CONNECTED:
          setStreamConnected(true);
          setDisplayWaitMessage(false);
          break;
        case EVENT_TYPE.CONNECTION_FAILED:
        case EVENT_TYPE.STREAM_FINISHED:
        case EVENT_TYPE.ERROR:
          setStreamConnected(false);
          dispatch(setStreamReady(false));
          break;
        case EVENT_TYPE.DATA:
          displayData(event.data);
          break;
        default:
          break;
      }
    }
  }

  useEffect(() => {
    streamSubject.addListener('TestInferenceObserver', new TestInferenceObserver());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  const module = usePostProcessingScript({
    projectId,
    applicationId,
    id: 'latest',
    hasPostProcessors:
      projectData.applications[applicationId]?.postProcessors !== undefined,
    fileName,
    postProcessorType: 'APPLICATION',
    executionContainer: 'BROWSER',
    viewOnly: true,
  });

  const displayData = (data?: Record<string, any>) => {
    if (module) {
      try {
        if ('displayVideoData' in module) {
          if (data && videoDataContainerRef.current && module.displayVideoData) {
            module.displayVideoData(videoDataContainerRef.current, data);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
  };

  if (!isTaskInProgress && !isOtherTaskInProgress && !deployment) {
    return (
      <NoDeployment
        imageUrl="images/emptyStateNoResults.png"
        message={<FormattedMessage id="test.resultsWillShow" />}
      />
    );
  }

  if (isStreamingInference) {
    if (displayWaitMessage) {
      return <WaitMessage />;
    }

    return (
      <section className="test-inference-results__video-container">
        <div className="test-inference-results__video">
          <div className="test-inference-results__video-main">
            <VideoStream
              hlsRef={videoController?.hlsClient.hlsRef}
              streamReady={streamReady}
              streamConnected={streamConnected}
              monitor={videoController?.monitor}
            />
            {/* Container that is populated with BROWSER post processor */}
            <div id="test-inference-results-video-data" ref={videoDataContainerRef} />
          </div>
          <MaxSecondsMessage />
          {segmentationLegends.length > 0 && (
            <SegmentationLegendView legends={segmentationLegends} placement="row" />
          )}
          {applicationId && projectId && displayRealtimeParameters && (
            <TestResultParameters
              projectId={projectId}
              applicationId={applicationId}
              spaId={spaId}
              useStreamingTask={false}
              videoController={videoController}
            />
          )}
        </div>
      </section>
    );
  }

  if (isTaskInProgress || isOtherTaskInProgress) {
    return <UnfinishedTestResults />;
  }

  if (deployment) {
    // For some reason, backend sends a string instead of JSON.
    // So string comparison is used to verify if results are empty
    const videoInputs = new Set(['WEB_CAMERA', 'IP_CAMERA', 'FILE', 'APP_INPUT']);
    const sourceType = deployment.deploymentInput?.inputSourceType || '-';
    const hasResults = deployment.deploymentType !== 'ONNX_WEB_INFERENCE';

    if (
      hasResults &&
      deployment.inferenceResults &&
      deployment.inferenceResults !== '{}' &&
      !videoInputs.has(sourceType)
    ) {
      return (
        <StaticTestResults
          projectId={projectId}
          search={search}
          deployment={deployment}
          deployConfig={deployConfig}
          projectData={projectData}
        />
      );
    }

    if (hasResults && hasTransformation) {
      return <ISPResult projectId={projectId} deploymentId={deployment.id} />;
    }

    return (
      <NoDeployment
        imageUrl="images/emptyStateNoResults.png"
        message={<FormattedMessage id="test.videoStreamingHasFinished" />}
      />
    );
  }

  return null;
};
